import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, NavLink, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useFeatures } from '../lib/features';
import { ROLE_LABELS } from '../mock';
import { initials } from '../lib/data';
import { useQueueCounts, useWorklistSummary } from '../api/hooks';
import Copilot from './Copilot';
import {
  Inbox, ShieldCheck, Activity, Calculator, Building2,
  Sparkles, Users, Network as NetworkIcon, FileText,
  Settings as SettingsIcon, LogOut, ChevronDown, Bell, Menu, Bot, Plus,
  ScrollText, PiggyBank, CalendarDays, Check,
} from 'lucide-react';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';

// Nav tells the product story: Today (do the work) → Revenue (the money it
// protects) → Analytics (how it's trending, incl. the AI agents) → Setup
// (configure once). Settings sits at the bottom.
const SECTIONS = [
  {
    label: 'Front desk',
    items: [
      { to: '/app', label: 'Worklist', icon: Inbox, end: true, badge: 'attention' },
      { to: '/app/appointments', label: 'Appointments', icon: CalendarDays },
      { to: '/app/eligibility', label: 'Verifications', icon: ShieldCheck },
      { to: '/app/patients', label: 'Patients', icon: Users },
    ],
  },
  {
    label: 'Revenue',
    items: [
      { to: '/app/finance', label: 'Finance', icon: PiggyBank },
      { to: '/app/estimator', label: 'Estimator', icon: Calculator, flag: 'estimator' },
    ],
  },
  {
    label: 'Analytics',
    items: [
      { to: '/app/insights', label: 'Insights', icon: Sparkles },
      { to: '/app/agents', label: 'Agent activity', icon: Activity },
    ],
  },
  {
    label: 'Setup',
    items: [
      { to: '/app/network', label: 'Network', icon: NetworkIcon },
      { to: '/app/contracts', label: 'Contracts', icon: FileText },
      { to: '/app/policies', label: 'Policies', icon: ScrollText },
    ],
  },
];

export default function AppLayout() {
  const { session, logout, switchRole, switchPractice } = useAuth();
  const { features, toggle } = useFeatures();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const practiceId = session?.practice?.id || 'prac_001';
  const { data: qc } = useQueueCounts(practiceId);
  const { data: summary = {} } = useWorklistSummary(practiceId);
  const badges = { workflows: qc?.open || 0, attention: summary.needsAttention || 0 };
  const ini = initials(session?.user?.name);
  const [copilotOpen, setCopilotOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setCopilotOpen((o) => !o); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const crumb = location.pathname.split('/')[2] || 'worklist';

  return (
    <div className="min-h-screen flex bg-[#f4f6f8]">
      {/* Sidebar — dark ink */}
      <aside className={`dh-sidebar fixed md:static inset-y-0 left-0 z-40 w-[244px] transform ${open ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 transition-transform flex flex-col`}>
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-[#1c2129]">
          <div className="h-8 w-8 rounded-lg bg-brand-500 text-white flex items-center justify-center font-bold shadow-sm">D</div>
          <div className="leading-tight">
            <div className="text-sm font-semibold text-white">Due Health</div>
            <div className="text-[10px] uppercase tracking-wider text-brand-300 font-medium">Eligibility AI</div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 text-sm">
          {SECTIONS.map((sec) => (
            <div key={sec.label} className="mb-5">
              <div className="px-2.5 mb-1.5 text-[10px] uppercase tracking-wider text-[#5b6373] font-semibold">{sec.label}</div>
              {sec.items.filter((item) => !item.flag || features[item.flag]).map((item) => {
                const Icon = item.icon;
                const count = item.badge ? badges[item.badge] : 0;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) => `dh-nav-link flex items-center gap-2.5 px-2.5 py-2 mb-0.5 ${isActive ? 'active' : ''}`}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1">{item.label}</span>
                    {count > 0 && (
                      <span className="text-[10px] font-semibold tnum bg-brand-500/20 text-brand-200 rounded-full px-1.5 py-0.5 min-w-[18px] text-center">{count}</span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-[#1c2129]">
          <NavLink to="/app/settings" onClick={() => setOpen(false)} className={({ isActive }) => `dh-nav-link flex items-center gap-2.5 px-2.5 py-2 ${isActive ? 'active' : ''}`}>
            <SettingsIcon className="h-4 w-4" /><span>Settings</span>
          </NavLink>
          <div className="mt-2 px-2.5 py-2 rounded-lg bg-[#161b22] border border-[#222834]">
            <div className="flex items-center gap-1.5 text-[11px] text-[#9aa2b1]">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 dh-pulse" />
              <span className="font-medium">Stedi sandbox · connected</span>
            </div>
            <div className="mt-1 flex items-center gap-1.5 text-[11px] text-violet-300">
              <Bot className="h-3 w-3" /> 7 agents active
            </div>
          </div>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-slate-900/30 z-30 md:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white/90 backdrop-blur border-b border-slate-200 flex items-center px-4 md:px-6 gap-3 sticky top-0 z-30">
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setOpen(!open)}>
            <Menu className="h-5 w-5" />
          </Button>
          <div className="hidden md:flex items-center gap-2 text-sm">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 hover:bg-slate-100">
                  <Building2 className="h-4 w-4 text-brand-600" />
                  <span className="font-medium text-slate-900">{session?.practice?.name}</span>
                  <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72">
                <DropdownMenuLabel className="text-xs text-slate-500">Practice</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => switchPractice('all')}>
                  <Building2 className="h-4 w-4 mr-2 text-slate-400" />
                  <span className="flex-1">All practices</span>
                  {session?.practice?.id === 'all' && <Check className="h-4 w-4 text-brand-600" />}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {(session?.practices || []).map((p) => (
                  <DropdownMenuItem key={p.id} onClick={() => switchPractice(p.id)}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-900 truncate">{p.name}</div>
                      <div className="text-[11px] text-slate-400">{p.specialty}</div>
                    </div>
                    {session?.practice?.id === p.id && <Check className="h-4 w-4 text-brand-600" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <span className="text-slate-300">/</span>
            <span className="capitalize text-slate-500">{crumb}</span>
          </div>
          <div className="flex-1 max-w-md mx-auto hidden lg:block">
            <button onClick={() => setCopilotOpen(true)} className="w-full relative text-left h-9 rounded-lg bg-slate-50 border border-slate-200 hover:border-brand-300 hover:bg-white transition-colors flex items-center pl-9 pr-12 text-sm text-slate-400">
              <Bot className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-violet-500" />
              Ask copilot or search…
              <span className="kbd absolute right-2.5 top-1/2 -translate-y-1/2">⌘K</span>
            </button>
          </div>
          <Link to="/app/eligibility/new" className="hidden sm:block">
            <Button className="bg-brand-600 hover:bg-brand-700 text-white h-9"><Plus className="h-4 w-4 mr-1.5" />Run check</Button>
          </Link>
          <Button variant="ghost" size="icon" className="relative">
            <Bell className="h-4 w-4" />
            {summary.needsAttention > 0 && <span className="absolute top-2 right-2 h-1.5 w-1.5 rounded-full bg-rose-500" />}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 hover:bg-slate-100 rounded-lg px-2 py-1.5">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-brand-100 text-brand-700 text-xs font-semibold">{ini}</AvatarFallback>
                </Avatar>
                <div className="text-left hidden sm:block">
                  <div className="text-sm font-medium text-slate-900 leading-tight">{session?.user?.name}</div>
                  <div className="text-[11px] text-slate-500">{ROLE_LABELS[session?.user?.role]}</div>
                </div>
                <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-xs text-slate-500">Switch role (demo)</DropdownMenuLabel>
              {Object.entries(ROLE_LABELS).map(([k, v]) => (
                <DropdownMenuItem key={k} onClick={() => switchRole(k)}>
                  <span className={`mr-2 h-2 w-2 rounded-full ${session?.user?.role === k ? 'bg-brand-500' : 'bg-slate-300'}`} />
                  {v}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-slate-500">Modules</DropdownMenuLabel>
              {/* Whole row toggles the module. onSelect+preventDefault keeps the
                  menu open after the click (Radix closes it otherwise), and a
                  full-width target beats a tiny switch that's easy to miss. */}
              <DropdownMenuItem className="flex items-center gap-2 cursor-pointer" onSelect={(e) => { e.preventDefault(); toggle('claimsDenials'); }}>
                <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-800">Claims &amp; Denials</div>
                  <div className="text-[11px] text-slate-400 leading-tight">Retrospective RCM context</div>
                </div>
                <Switch checked={features.claimsDenials} tabIndex={-1} className="pointer-events-none" />
              </DropdownMenuItem>
              <DropdownMenuItem className="flex items-center gap-2 cursor-pointer" onSelect={(e) => { e.preventDefault(); toggle('estimator'); }}>
                <Calculator className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-800">Estimator</div>
                  <div className="text-[11px] text-slate-400 leading-tight">Good-faith estimate page</div>
                </div>
                <Switch checked={features.estimator} tabIndex={-1} className="pointer-events-none" />
              </DropdownMenuItem>
              <DropdownMenuItem className="flex items-center gap-2 cursor-pointer" onSelect={(e) => { e.preventDefault(); toggle('priorAuth'); }}>
                <ShieldCheck className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-800">Prior authorization</div>
                  <div className="text-[11px] text-slate-400 leading-tight">Authorization queue + packets</div>
                </div>
                <Switch checked={features.priorAuth} tabIndex={-1} className="pointer-events-none" />
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild><Link to="/app/settings" className="flex w-full">Settings</Link></DropdownMenuItem>
              <DropdownMenuItem onClick={logout}>
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 p-4 md:p-6 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>

      <Copilot open={copilotOpen} onClose={() => setCopilotOpen(false)} />
    </div>
  );
}
